import { useRef } from "react";
import type { CmmdProjectTaskDraftProposal } from "@cmmd-forge/contracts";

const protectedRootStorageKey = (draftId: number) => `forge:project-lead-root:${draftId}`;

function persistedProtectedRoot(
  draftId: number,
  tasks: CmmdProjectTaskDraftProposal["tasks"],
): string {
  const fallback =
    tasks
      .filter((task) => task.parentTaskRef === null)
      .map((task) => task.localRef)
      .toSorted()[0] ?? "";
  if (draftId < 0) return fallback;
  try {
    const key = protectedRootStorageKey(draftId);
    const stored = window.localStorage.getItem(key);
    if (stored !== null && tasks.some((task) => task.localRef === stored)) return stored;
    window.localStorage.setItem(key, fallback);
    return fallback;
  } catch {
    return fallback;
  }
}

export function useProtectedProjectLeadRoot(
  draftId: number,
  tasks: CmmdProjectTaskDraftProposal["tasks"],
): string {
  const protectedRootRef = useRef<{ readonly draftId: number; readonly localRef: string } | null>(
    null,
  );
  const cachedRootStillExists = tasks.some(
    (task) => task.localRef === protectedRootRef.current?.localRef && task.parentTaskRef === null,
  );
  if (protectedRootRef.current?.draftId !== draftId || !cachedRootStillExists) {
    protectedRootRef.current = {
      draftId,
      localRef: persistedProtectedRoot(draftId, tasks),
    };
  }
  return protectedRootRef.current.localRef;
}
